import axios from 'axios';

// Normalize base URL to always include '/api' suffix
const rawBase = process.env.REACT_APP_API_URL || '';
let normalizedBase = '/api';
if (rawBase) {
  const trimmed = rawBase.replace(/\/$/, '');
  normalizedBase = trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
}

// Create axios instance
const apiInstance = axios.create({
  baseURL: normalizedBase,
  timeout: 60000, // Increased to 60 seconds for large payloads
  maxContentLength: 50 * 1024 * 1024, // 50MB
  maxBodyLength: 50 * 1024 * 1024, // 50MB
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// ─── 403 blocked toast notification ───────────────────────────────────────────
// Shows a user-friendly notification when an API call is blocked by geo/IP/fingerprint blocking
let blockedToastTimer = null;
// Escape user-controlled text before injecting into innerHTML (defense-in-depth
// against any future backend change that could put untrusted content in `message`).
const escapeHtml = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
})[c]);
const showBlockedToast = (reason, message, fingerprint) => {
  // Remove any existing blocked toast
  const existing = document.getElementById('ags-blocked-toast');
  if (existing) existing.remove();
  if (blockedToastTimer) clearTimeout(blockedToastTimer);

  const toast = document.createElement('div');
  toast.id = 'ags-blocked-toast';
  toast.style.cssText = `
    position:fixed; top:0; left:0; right:0; z-index:99999;
    background:#fef2f2; border-bottom:2px solid #ef4444;
    padding:16px 20px; display:flex; align-items:center; justify-content:center; gap:12px;
    font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
    box-shadow:0 2px 8px rgba(0,0,0,0.1);
  `;
  const safeMessage = escapeHtml(message || 'Access restricted.');
  const safeRef = fingerprint ? ` <span style="font-family:monospace;font-size:12px;color:#888;">Ref: ${escapeHtml(fingerprint)}</span>` : '';
  toast.innerHTML = `
    <span style="font-size:20px;">&#9888;</span>
    <span style="font-size:14px; color:#991b1b;">${safeMessage}${safeRef}</span>
    <span style="font-size:13px; color:#6b7280;">· support@autoglassshop.com.au · 1300 002 345</span>
  `;
  document.body.appendChild(toast);
  blockedToastTimer = setTimeout(() => {
    if (toast.parentNode) toast.remove();
  }, 15000);  // auto-dismiss after 15s
};

// Generate or retrieve guest session ID
const getGuestSessionId = () => {
  let sessionId = localStorage.getItem('guest_session_id');
  if (!sessionId) {
    sessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('guest_session_id', sessionId);
  }
  return sessionId;
};

// Request interceptor to attach the token and log large requests
apiInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    const hasValidToken = !!token && token !== 'null' && token !== 'undefined';
    if (hasValidToken) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // For guest users, attach session ID so cart persists
      config.headers['x-session-id'] = getGuestSessionId();
    }

    // Ensure product availability respects selected viewing branch
    try {
      const isGet = (config.method || 'get').toLowerCase() === 'get';
      const urlStr = config.url || '';
      // Only target product endpoints
      if (isGet && typeof urlStr === 'string' && (urlStr.startsWith('/products') || urlStr.startsWith('/api/products'))) {
        const viewRaw = localStorage.getItem('view_warehouse');
        const guestRaw = localStorage.getItem('guest_warehouse');
        const sourceRaw = viewRaw || guestRaw;
        if (sourceRaw) {
          const sel = JSON.parse(sourceRaw);
          const viewId = sel && sel.id ? String(sel.id) : null;
          if (viewId) {
            const dummyBase = (process.env.REACT_APP_API_URL || '').startsWith('http')
              ? process.env.REACT_APP_API_URL
              : window.location.origin;
            const u = new URL(urlStr, dummyBase);
            u.searchParams.set('view_warehouse_id', viewId);
            // Preserve relative URL
            config.url = u.pathname + (u.search ? `?${u.searchParams.toString()}` : '');
            // Do not set a custom header to avoid CORS preflight issues
          }
        }
      }
    } catch (e) {
      console.warn('Failed to append view_warehouse_id:', e);
    }

    // Log large request payloads
    if (config.data && JSON.stringify(config.data).length > 10000) {
      console.warn('Large request payload detected:', {
        url: config.url,
        method: config.method,
        size: JSON.stringify(config.data).length,
      });
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// ─── Single-flight token refresh ─────────────────────────────────────────────
// Ensures only one /auth/refresh call is in flight at a time. Parallel requests
// that detect an auth issue will all await the same refresh promise, preventing
// concurrent refresh calls that could invalidate each other's tokens.
let refreshPromise = null;

function doRefresh() {
  const refreshToken = localStorage.getItem('refresh_token');
  const hasValidRefresh = !!refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined';
  if (!hasValidRefresh) {
    return Promise.reject(new Error('No refresh token available'));
  }

  return axios.post(`${apiInstance.defaults.baseURL}/auth/refresh`, { refresh_token: refreshToken })
    .then((response) => {
      // Store new tokens BEFORE resolving so retry reads the fresh token
      const { access_token, refresh_token } = response.data.tokens;
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      return access_token;
    });
}

function getRefreshedToken() {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// ─── Response interceptor ────────────────────────────────────────────────────
// Two layers:
//   1. Success handler: checks X-Auth-Status header from optionalAuth middleware.
//      If the header signals an auth issue (TOKEN_EXPIRED, SESSION_MISMATCH, etc.),
//      silently refreshes the token and retries the request once. On refresh
//      failure, clears tokens but does NOT redirect — the page is optional-auth
//      and should still work with public pricing.
//   2. Error handler: handles hard 401s from requireAuth-protected routes.
//      Attempts refresh, retries once, and redirects to login on failure.
apiInstance.interceptors.response.use(
  (response) => {
    const authStatus = response.headers['x-auth-status'];

    // No auth issue or already retried — return as-is
    if (!authStatus || authStatus === 'OK' || response.config._authRetried) {
      return response;
    }

    // Auth issue detected on an optional-auth route — attempt silent refresh + retry
    response.config._authRetried = true;
    return getRefreshedToken()
      .then((newToken) => {
        response.config.headers['Authorization'] = `Bearer ${newToken}`;
        return apiInstance(response.config);
      })
      .catch(() => {
        // Refresh failed — clear stale tokens but don't redirect.
        // The user stays on the page with public pricing; next navigation
        // to a requireAuth route will trigger the login redirect naturally.
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        // Return the original (public-priced) response so the page still renders
        return response;
      });
  },
  async (error) => {
    const originalRequest = error.config;

    // ── 403/451 handler: geo-blocking / IP block / device block ──
    // 403 = IP/fingerprint/honeypot block, 451 = geo-block (country/state)
    // Show a user-friendly toast notification (don't redirect — the app is already loaded)
    if ((error.response?.status === 403 || error.response?.status === 451) && error.response?.data?.reason) {
      const { reason, message, fingerprint } = error.response.data;
      // Only show toast once per block reason (avoid spamming on every API call)
      if (!originalRequest._blockToastShown) {
        originalRequest._blockToastShown = true;
        showBlockedToast(reason, message, fingerprint);
      }
      return Promise.reject(error);
    }

    // If error is not 401, or if we've already tried to refresh the token, reject
    // Also skip refresh for auth endpoints where 401 is expected (login failures, locked accounts)
    const url = originalRequest?.url || '';
    const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/register');
    if (error.response?.status !== 401 || originalRequest._retry || isAuthEndpoint) {
      // Suppress logging for /auth/me 401 errors - they're expected when token is invalid
      // Suppress logging for /customers/profile 404 - expected when user has no linked customer record
      const isAuthMeEndpoint = originalRequest?.url?.includes('/auth/me');
      const isCustomersProfileEndpoint = originalRequest?.url?.includes('/customers/profile');
      const is401 = error.response?.status === 401;
      const is404 = error.response?.status === 404;
      
      if (!(isAuthMeEndpoint && is401) && !(isCustomersProfileEndpoint && is404)) {
        console.error('API Error:', {
          message: error.message,
          status: error.response?.status,
          url: originalRequest?.url,
          method: originalRequest?.method,
          data: error.response?.data,
        });
      }
      return Promise.reject(error);
    }

    // Mark that we're retrying this request
    originalRequest._retry = true;

    try {
      const newToken = await getRefreshedToken();
      originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
      return apiInstance(originalRequest);
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
      // Clear tokens and redirect to login on refresh failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
      return Promise.reject(refreshError);
    }
  }
);

export default apiInstance;
